/**
 * AttachmentDetailDialog Component
 *
 * Shows attachment preview (for images) and metadata.
 * Allows editing tags and downloading the file.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  TextField,
  Autocomplete,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { api } from '@/lib/api';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import TableChartIcon from '@mui/icons-material/TableChart';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useUpdateAttachment } from '@/hooks/useAttachments';
import { useNotify } from '@/hooks/useNotify';
import type { Attachment, AttachmentEntityType } from '@/types';
import { formatDate } from '@/utils/locale';

interface AttachmentDetailDialogProps {
  open: boolean;
  attachment: Attachment;
  onClose: () => void;
  onDownload: (attachment: Attachment) => void;
  onDelete: (attachment: Attachment) => void;
  /**
   * View-only mode: hides the delete action and locks tag editing. Used when the
   * attachment is only shown here by inheritance (e.g. a work-order attachment
   * surfaced on the vehicle, or a vehicle attachment surfaced on a work order),
   * so it can be previewed/downloaded but must be managed from its own context (#1043).
   */
  readOnly?: boolean;
  /** Ordered attachments belonging to this card, excluding inherited files. */
  galleryAttachments?: Attachment[];
  onNavigate?: (attachment: Attachment) => void;
}

function getFileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') return <PictureAsPdfIcon sx={{ fontSize: 64 }} />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel'))
    return <TableChartIcon sx={{ fontSize: 64 }} />;
  if (mimeType.includes('document') || mimeType.includes('msword') || mimeType.includes('text/'))
    return <DescriptionIcon sx={{ fontSize: 64 }} />;
  return <InsertDriveFileIcon sx={{ fontSize: 64 }} />;
}

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getEntityLabel(
  entityType: AttachmentEntityType,
  t: (key: string) => string,
  tc: (key: string) => string
): string {
  const labelMap: Record<AttachmentEntityType, string> = {
    booking: t('attachments.entityBooking'),
    vehicle: t('attachments.entityVehicle'),
    customer: t('attachments.entityCustomer'),
    inspection: t('attachments.entityInspection'),
    person: tc('attachments.entityPerson'),
    company: tc('attachments.entityCompany'),
    courtesy_loan: tc('attachments.entityCourtesyLoan'),
  };
  return labelMap[entityType] ?? entityType;
}

/** Remounted for each selection so a late URL/image response cannot replace the current preview. */
function ImagePreview({ attachment }: { attachment: Attachment }) {
  const { t } = useTranslation('workorders');
  const [url, setUrl] = useState<string>();
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    void api
      .get<{ downloadUrl: string }>(`/attachments/${attachment.id}/download-url`)
      .then(({ downloadUrl }) => {
        if (active) setUrl(downloadUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [attachment.id]);

  if (failed) return <Alert severity="warning">{t('attachments.previewFailed')}</Alert>;

  return (
    <>
      {!loaded && <CircularProgress aria-label={t('attachments.previewLoading')} />}
      {url && (
        <Box
          component="img"
          src={url}
          alt={attachment.originalFileName}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          draggable={false}
          sx={{
            display: loaded ? 'block' : 'none',
            maxWidth: '100%',
            height: '60vh',
            objectFit: 'contain',
            borderRadius: 1,
          }}
        />
      )}
    </>
  );
}

export function AttachmentDetailDialog({
  open,
  attachment,
  onClose,
  onDownload,
  onDelete,
  readOnly = false,
  galleryAttachments = [],
  onNavigate,
}: AttachmentDetailDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateAttachment = useUpdateAttachment();

  const titleId = useId();
  // Keep edits with their attachment when browsing away and back.
  const [tagDrafts, setTagDrafts] = useState<Record<string, { tags: string[]; changed: boolean }>>(
    {}
  );
  const tags = tagDrafts[attachment.id]?.tags ?? attachment.tags;
  const tagsChanged = tagDrafts[attachment.id]?.changed ?? false;
  const index = galleryAttachments.findIndex((item) => item.id === attachment.id);
  const previous = index > 0 ? galleryAttachments[index - 1] : undefined;
  const next = index >= 0 ? galleryAttachments[index + 1] : undefined;
  const showNavigation = !!onNavigate && index >= 0 && galleryAttachments.length > 1;
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const isImage = attachment.mimeType.startsWith('image/');

  const handleSaveTags = async () => {
    try {
      await updateAttachment.mutateAsync({ id: attachment.id, tags });
      setTagDrafts((drafts) => ({
        ...drafts,
        [attachment.id]: { tags, changed: false },
      }));
      notify.success(t('attachments.tagsSaved'));
    } catch {
      notify.error(t('attachments.tagsError'));
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        if (
          event.altKey ||
          event.ctrlKey ||
          event.metaKey ||
          event.shiftKey ||
          !(event.target instanceof Element) ||
          event.target.closest(
            'input, textarea, select, [contenteditable="true"], [role="combobox"], [data-attachment-tags]'
          )
        )
          return;
        const target =
          event.key === 'ArrowLeft' ? previous : event.key === 'ArrowRight' ? next : undefined;
        if (target && onNavigate) {
          event.preventDefault();
          event.stopPropagation();
          onNavigate(target);
        }
      }}
      fullWidth
      mobileTitle={t('attachments.fileDetails')}
    >
      <DialogTitle id={titleId}>{t('attachments.fileDetails')}</DialogTitle>
      <DialogContent>
        {showNavigation && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 2,
            }}
          >
            <Button
              startIcon={<ChevronLeftIcon />}
              disabled={!previous}
              onClick={() => previous && onNavigate?.(previous)}
            >
              {t('attachments.previous')}
            </Button>
            <Typography variant="body2" role="status">
              {t('attachments.galleryPosition', {
                current: index + 1,
                total: galleryAttachments.length,
              })}
            </Typography>
            <Button
              endIcon={<ChevronRightIcon />}
              disabled={!next}
              onClick={() => next && onNavigate?.(next)}
            >
              {t('attachments.next')}
            </Button>
          </Box>
        )}
        {/* Swipe only the preview, leaving metadata and tag inputs free to scroll/edit. */}
        <Box
          onTouchStart={(event) => {
            const touch = event.touches.length === 1 ? event.touches[0] : undefined;
            touchStart.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
          }}
          onTouchCancel={() => {
            touchStart.current = null;
          }}
          onTouchEnd={(event) => {
            const start = touchStart.current;
            touchStart.current = null;
            const touch = event.changedTouches[0];
            if (!start || !touch) return;
            const dx = touch.clientX - start.x;
            const dy = touch.clientY - start.y;
            if (Math.abs(dx) < 50 || Math.abs(dx) <= Math.abs(dy)) return;
            const target = dx > 0 ? previous : next;
            if (target) onNavigate?.(target);
          }}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: isImage ? '40vh' : 120,
            mb: 2,
          }}
        >
          {isImage && open ? (
            <ImagePreview key={attachment.id} attachment={attachment} />
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 3,
                color: 'action.active',
              }}
            >
              {getFileIcon(attachment.mimeType)}
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('attachments.previewUnavailable')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Metadata */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {t('attachments.fileName')}
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere', textAlign: 'right' }}>
              {attachment.originalFileName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {t('attachments.fileSize')}
            </Typography>
            <Typography variant="body2">{formatFileSize(attachment.fileSizeBytes)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {t('attachments.fileType')}
            </Typography>
            <Typography variant="body2">{attachment.mimeType}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {t('attachments.uploadedAt')}
            </Typography>
            <Typography variant="body2">
              {formatDate(attachment.createdAt)}
            </Typography>
          </Box>
        </Box>

        {/* Entity links */}
        {attachment.entityLinks.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('attachments.linkedTo')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {attachment.entityLinks.map((link) => (
                <Chip
                  key={link.id}
                  label={getEntityLabel(link.entityType, t, tc)}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Tags */}
        <Autocomplete
          key={attachment.id}
          data-attachment-tags
          disabled={updateAttachment.isPending}
          multiple
          freeSolo
          autoSelect
          readOnly={readOnly}
          options={[]}
          value={tags}
          onChange={(_e, newValue) => {
            setTagDrafts((drafts) => ({
              ...drafts,
              [attachment.id]: { tags: newValue, changed: true },
            }));
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return <Chip key={key} label={option} size="small" {...tagProps} />;
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('attachments.tags')}
              placeholder={t('attachments.addTag')}
              size="small"
            />
          )}
        />

        {!readOnly && tagsChanged && (
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSaveTags}
              disabled={updateAttachment.isPending}
            >
              {tc('save')}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!readOnly && (
          <Button color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(attachment)}>
            {tc('delete')}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>{tc('close')}</Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => onDownload(attachment)}
        >
          {t('attachments.download')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
